// /////////////////////////////////////////////////////////////
// [ファイル] MarkdownMessage.tsx
// [概要] AI 応答などの Markdown 文字列を MUI のスタイルで表示する共通コンポーネント。
//        markdown-to-jsx を用い、見出し/リスト/コード/リンク等を MUI 要素へマッピングする。
//        本文中に書いた生HTML(セル結合テーブル/CSSカード/SVG図解)も解釈・整形する
//        (markdown-to-jsx は既定で inline HTML を描画。<script> は実行しない静的描画)。
//        ・テーブルのセル結合 … <table> に <td colspan> / <th rowspan> をそのまま記述
//        ・カード図解 … <div class="iso-cards"><div class="iso-card">…</div></div>
//        ・図形図解 … <svg>…</svg> (静的。canvas は JS 実行不可のため使用不可)
// /////////////////////////////////////////////////////////////
import React from 'react';
import Markdown from 'markdown-to-jsx';
import { Box, Typography, Link, Divider } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';

interface Props {
  // 表示する Markdown テキスト
  children: string;
}

// CommonMark の flanking(隣接)規則により、閉じ **  の直前が日本語の句読点(例 。)で
// 直後が CJK 文字だと太字として解釈されず ** が直書きされてしまう。これを避けるため、
// コードを退避したうえで **…** を明示的に <strong> へ変換する。
function fixBoldForCjk(md: string): string {
  const stash: string[] = [];
  // 行内コード・コードフェンス内の ** は変換対象から除外する
  const protectedMd = md
    .replace(/```[\s\S]*?```/g, (m) => `\x01${stash.push(m) - 1}\x01`)
    .replace(/`[^`\n]*`/g, (m) => `\x01${stash.push(m) - 1}\x01`);
  const converted = protectedMd.replace(
    /(?<!\*)\*\*(?!\s)([^\n]+?)\*\*(?!\*)/g,
    (_m, inner) => `<strong>${inner}</strong>`
  );
  return converted.replace(/\x01(\d+)\x01/g, (_m, i) => stash[Number(i)]);
}

// markdown-to-jsx の要素オーバーライド定義 (MUI 要素へマッピング)
const overrides = {
  p: {
    component: Typography,
    props: { variant: 'body2', sx: { lineHeight: 1.6, my: 0.75 } },
  },
  h1: {
    component: Typography,
    props: { variant: 'subtitle1', sx: { fontWeight: 700, mt: 1.5, mb: 0.5 } },
  },
  h2: {
    component: Typography,
    props: { variant: 'subtitle1', sx: { fontWeight: 700, mt: 1.5, mb: 0.5 } },
  },
  h3: {
    component: Typography,
    props: { variant: 'subtitle2', sx: { fontWeight: 700, mt: 1.25, mb: 0.5 } },
  },
  ul: {
    component: Box,
    props: { component: 'ul', sx: { pl: 2.5, my: 0.5 } },
  },
  ol: {
    component: Box,
    props: { component: 'ol', sx: { pl: 2.5, my: 0.5 } },
  },
  li: {
    component: Box,
    props: {
      component: 'li',
      sx: { typography: 'body2', lineHeight: 1.6, my: 0.25 },
    },
  },
  a: {
    component: Link,
    props: { target: '_blank', rel: 'noopener noreferrer' },
  },
  code: {
    component: Box,
    props: {
      component: 'code',
      sx: {
        bgcolor: 'rgba(0,0,0,0.06)',
        px: 0.5,
        py: 0.1,
        borderRadius: 0.5,
        fontFamily: 'monospace',
        fontSize: '0.85em',
      },
    },
  },
  pre: {
    component: Box,
    props: {
      component: 'pre',
      sx: {
        bgcolor: 'rgba(0,0,0,0.06)',
        p: 1.25,
        borderRadius: 1,
        overflowX: 'auto',
        my: 0.75,
        fontFamily: 'monospace',
        fontSize: '0.85em',
        whiteSpace: 'pre',
      },
    },
  },
  blockquote: {
    component: Box,
    props: {
      component: 'blockquote',
      sx: {
        borderLeft: 3,
        borderColor: 'divider',
        pl: 1.5,
        my: 0.75,
        color: 'text.secondary',
      },
    },
  },
  hr: { component: Divider, props: { sx: { my: 1 } } },
  strong: {
    component: Box,
    props: { component: 'strong', sx: { fontWeight: 700 } },
  },
  // GFM テーブル (Docs 由来の相関表など)。table 自体を横スクロール可能にし、
  // th/td へ罫線・余白を付けて可読性を確保する。
  table: {
    component: Box,
    props: {
      component: 'table',
      sx: {
        display: 'block',
        overflowX: 'auto',
        maxWidth: '100%',
        borderCollapse: 'collapse',
        my: 1,
        fontSize: '0.8rem',
        '& th, & td': {
          border: '1px solid',
          borderColor: 'divider',
          px: 1,
          py: 0.5,
          textAlign: 'left',
          verticalAlign: 'top',
          whiteSpace: 'nowrap',
        },
        '& th': { bgcolor: 'action.hover', fontWeight: 700 },
      },
    },
  },
};

// 本文中に書いた生HTML(カード図解/SVG/セル結合表)向けの追加スタイル。
// クラス名でスタイルを当てるため sx のネスト子孫セレクタで指定する
// (markdown-to-jsx は HTML の class を className として保持する)。
const htmlAuthoringSx: SxProps<Theme> = {
  // 先頭/末尾要素の余白は詰める
  '& > *:first-of-type': { mt: 0 },
  '& > *:last-child': { mb: 0 },

  // --- カード図解 (<div class="iso-cards"> にカードを並べる) ---
  '& .iso-cards': {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: 1,
    my: 1,
  },
  '& .iso-card': {
    display: 'flex',
    flexDirection: 'column',
    gap: 0.5,
    p: 1.25,
    border: '1px solid',
    borderColor: 'divider',
    borderRadius: 1.5,
    bgcolor: 'background.paper',
    boxShadow: 1,
  },
  '& .iso-card__title': { fontWeight: 700, fontSize: '0.9rem', color: 'primary.main' },
  '& .iso-card__body': { fontSize: '0.85rem', lineHeight: 1.6, color: 'text.secondary' },
  '& .iso-card__meta': { fontSize: '0.75rem', color: 'text.disabled' },

  // --- SVG 図解 (箱・矢印・フロー図など静的な図形) ---
  '& svg': { maxWidth: '100%', height: 'auto', display: 'block', my: 1 },
  '& figure': { m: 0, my: 1 },
  '& figcaption': { fontSize: '0.75rem', color: 'text.secondary', mt: 0.5, textAlign: 'center' },

  // --- セル結合テーブルで長文を折り返したい場合の opt-in ---
  //     <table class="iso-table--wrap"> と書くと nowrap を解除して折り返す
  '& table.iso-table--wrap th, & table.iso-table--wrap td': { whiteSpace: 'normal' },
};

function MarkdownMessage({ children }: Props) {
  return (
    <Box sx={htmlAuthoringSx}>
      <Markdown options={{ overrides, forceBlock: true }}>{fixBoldForCjk(children)}</Markdown>
    </Box>
  );
}

// 本文 (文字列) が同じなら再描画しない。会話画面は Firestore の更新通知のたびに全メッセージを
// 描き直すため、過去の回答の Markdown 解析 (表・SVG を含むと重い) を毎回やり直さないようにする
export default React.memo(MarkdownMessage);
